import { useCallback, useEffect } from "react";
import { TimberStorage, CreateTimberStorage, UpdateTimberStorage } from "../types/models/TimberStorage.d";
import { UpdateEntityTimberStorage } from "../types/models/TimberStorage";
import { useGetTimberStorageListQuery, useInsertTimberStorageMutation, useUpdateTimberStorageMutation, useDeleteTimberStorageMutation, } from "../apis/TimberStorage.api";
import { useDeleteDialog, useDescriptionAlert } from '../../share';
import { useNavigate } from "react-router";
import { t } from "@lingui/core/macro";

export default function useTimberStorage(page: number, pageSize: number) {

    const navigate = useNavigate()
    const { openDialog, closeDialog } = useDeleteDialog();

    const { openSuccessAlert, openFailAlert } = useDescriptionAlert()

    const [insertTimberStorageMutation, { 
        isLoading: insertTimberStorageLoading, 
        isSuccess: insertTimberStorageIsSuccess,
        isError: insertTimberStorageIsError,
        error: insertTimberStorageError,
    }] = useInsertTimberStorageMutation()
    const [updateTimberStorageMutation, { 
        isLoading: updateTimberStorageLoading,
        isSuccess: updateTimberStorageIsSuccess,
        isError: updateTimberStorageIsError,
        error: updateTimberStorageError,
    }] = useUpdateTimberStorageMutation()
    const [deleteTimberStorageMutation, { 
        isLoading: deleteTimberStorageLoading,
        isSuccess: deleteTimberStorageIsSuccess,
        isError: deleteTimberStorageIsError,
        error: deleteTimberStorageError,
    }] = useDeleteTimberStorageMutation()

    const loading =
        insertTimberStorageLoading ||
        updateTimberStorageLoading ||
        deleteTimberStorageLoading ||
        false

    const insertTimberStorage = useCallback(async (timberStorage: CreateTimberStorage, redirectPath: string|undefined) => {
        try {
            const data = await insertTimberStorageMutation(timberStorage).unwrap()
            if (data) {
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate(`../${data.id}`, {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("add error", e)
            openFailAlert(t`Add error: ` + e.toString())
        }
    }, [insertTimberStorageMutation, navigate])
    
    useEffect(() => {
        if (insertTimberStorageIsSuccess) {
            openSuccessAlert('Timber Storage created')
        } else if (insertTimberStorageIsError) {
            console.log("add error", insertTimberStorageError)
            openFailAlert(t`Timber Storage add error: ` + insertTimberStorageError?.data?.message)
        }
    }, [
        insertTimberStorageIsSuccess,
        insertTimberStorageIsError,
        insertTimberStorageError
    ]);

    const updateTimberStorage = useCallback(async (timberStorage: UpdateEntityTimberStorage, redirectPath: string|undefined) => {
        try {
            const data = await updateTimberStorageMutation(timberStorage).unwrap();
            if (data != null) {
                //if update success
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate('..', {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("update error", e)
            openFailAlert(t`Update error: ` + e.toString())
        }
    }, [updateTimberStorageMutation, navigate])
    
    useEffect(() => {
        if (updateTimberStorageIsSuccess) {
            openSuccessAlert(t`Timber Storage updated`)
        } else if (updateTimberStorageIsError) {
            console.log("update error", updateTimberStorageError)
            openFailAlert(t`Timber Storage update error: ` + updateTimberStorageError?.data?.message)
        }
    }, [
        updateTimberStorageIsSuccess,
        updateTimberStorageIsError,
        updateTimberStorageError
    ]);

    const deleteTimberStorage = useCallback((timberStorage: TimberStorage, redirectPath: string|undefined) => {
        openDialog(async () => {
            timberStorage = {
                ...timberStorage,
            }
            try {
                const { data } = await deleteTimberStorageMutation(timberStorage);
                if (data != null) {
                    if (redirectPath) {
                        navigate(redirectPath, {relative: 'path'})
                    }
                }
            } catch (e) {
                console.log("delete error", e)
                openFailAlert(t`Delete error: ` + e.toString())
            }
            closeDialog()
        });
    }, [deleteTimberStorageMutation, closeDialog, openDialog])
    
    useEffect(() => {
        if (deleteTimberStorageIsSuccess) {
            openSuccessAlert(t`Timber Storage deleted`)
        } else if (deleteTimberStorageIsError) {
            console.log("delete error", deleteTimberStorageError)
            openFailAlert(t`Timber Storage delete error: ` + deleteTimberStorageError?.data?.message)
        }
    }, [
        deleteTimberStorageIsSuccess,
        deleteTimberStorageIsError,
        deleteTimberStorageError
    ]);

    return {
        loading,
        insertTimberStorage,
        updateTimberStorage,
        deleteTimberStorage
    }
}
